import type { Client } from '@accounts/transport';
import type { Member, Role } from './types';

/** What one product holds for an organization or a member, in that product's words */
export interface ProductHoldings {
	product: string;
	state: 'answered' | 'unavailable';
	items: { kind: string; count: number; label: string; consequence: string; billing?: boolean; link?: string }[];
}

/** What each role may do in one product, as the product publishes it */
export interface ProductRoles {
	product: string;
	state: 'answered' | 'unavailable';
	roles: Record<string, string[]>;
}

/** A member's open request for a higher role */
export interface RoleRequest {
	id: string;
	account: string;
	name?: string;
	role: Role;
	current?: Role;
	note?: string;
	created: string;
}

/** One line of an organization's audit log */
export interface AuditEntry {
	seq: number;
	at: string;
	action: string;
	actor?: { id: string; name?: string };
	subject?: { id: string; name?: string };
	role?: Role;
}

/** An organization in its grace before deletion */
export interface Closing {
	organization: string;
	name: string;
	role: Role;
	purge: string;
}

/**
 * What an organization's people do about the organization itself (ACC-05, ACC-06, ACC-14, ACC-20,
 * ACC-21, ARR-11…ARR-14): the members list by role, ownership, leaving, deleting within a grace, what
 * the products hold, role requests, what roles mean in each product, the audit log and the logo.
 * Every rule is the service's; this class carries its refusals back.
 */
export class Stewardship {
	#client: Client;

	constructor(client: Client) {
		this.#client = client;
	}

	static #path(organization: string, rest = ''): string {
		return `/v1/organizations/${encodeURIComponent(organization)}${rest}`;
	}

	async members(organization: string, all = false): Promise<Member[]> {
		return (await this.#client.get<{ members: Member[] }>(Stewardship.#path(organization, `/members${all ? '?state=all' : ''}`))).members;
	}

	/** Hands ownership to a member; confirmed by the typed name, so never sent again after a renewal */
	async transfer(organization: string, account: string, confirm: string): Promise<void> {
		await this.#client.post(Stewardship.#path(organization, '/ownership'), { account, confirm }, { replay: false });
	}

	async leave(organization: string): Promise<void> {
		await this.#client.delete(Stewardship.#path(organization, '/membership'), { replay: false });
	}

	/**
	 * What each product holds. Advisory: a read that fails (the session included) is the caller's to
	 * answer as "could not be checked", so it never puts a dialog in front of the confirmation it serves
	 */
	async holdings(organization: string, member?: string): Promise<ProductHoldings[]> {
		const path = member ? `/members/${encodeURIComponent(member)}/holdings` : '/holdings';
		return (await this.#client.get<{ holdings: ProductHoldings[] }>(Stewardship.#path(organization, path), { ended: 'caller' })).holdings;
	}

	/** Starts the deletion; answers when it is purged */
	async close(organization: string, confirm: string): Promise<{ purge: string }> {
		return (await this.#client.call<{ organization: { purge: string } }>('DELETE', Stewardship.#path(organization), { confirm }, { replay: false })).organization;
	}

	async restore(organization: string): Promise<void> {
		await this.#client.post(Stewardship.#path(organization, '/restoration'), {}, { replay: false });
	}

	async closing(): Promise<Closing[]> {
		return (await this.#client.get<{ closing?: Closing[] }>('/v1/organizations')).closing ?? [];
	}

	/** A read of the page like any other: a session that ended meanwhile is the family's dialog, and the read waits for it */
	async requests(organization: string): Promise<RoleRequest[]> {
		return (await this.#client.get<{ requests: RoleRequest[] }>(Stewardship.#path(organization, '/requests'))).requests;
	}

	async ask(organization: string, role: Role, note?: string): Promise<RoleRequest> {
		return (await this.#client.post<{ request: RoleRequest }>(Stewardship.#path(organization, '/requests'), { role, note })).request;
	}

	async decline(organization: string, request: string): Promise<void> {
		await this.#client.post(Stewardship.#path(organization, `/requests/${encodeURIComponent(request)}/decline`), {}, { replay: false });
	}

	async roles(organization: string): Promise<ProductRoles[]> {
		return (await this.#client.get<{ products: ProductRoles[] }>(Stewardship.#path(organization, '/roles'))).products;
	}

	async audit(organization: string, before?: number): Promise<{ entries: AuditEntry[]; next?: number }> {
		return this.#client.get<{ entries: AuditEntry[]; next?: number }>(Stewardship.#path(organization, `/audit${before ? `?before=${before}` : ''}`));
	}

	/** Uploads a logo; the service decides its type from its bytes */
	async logo(organization: string, file: Blob): Promise<string> {
		return (await this.#client.upload<{ logo: string }>('PUT', Stewardship.#path(organization, '/logo'), file)).logo;
	}

	async unlogo(organization: string): Promise<void> {
		await this.#client.delete(Stewardship.#path(organization, '/logo'), { replay: false });
	}
}
